<template>
  <div class="camp-program-page">
    <b-container class="py-5">
      <template v-if="loading.program">
        <div class="loading-block">
          <b-spinner variant="primary"></b-spinner>
        </div>
      </template>

      <template v-else>
        <b-card no-body class="program-detail-card">
          <figure class="program-cover mb-0">
            <b-card-img
              :src="getProgramCover(program)"
              :alt="program.Name"
              @error="setDefaultCover"
            ></b-card-img>
          </figure>
          <b-card-body>
            <h2>{{ program.Name }}</h2>
            <div
              v-if="hasProgramPrice"
              :class="isProgramEarlyBirdActive ? 'program-early-bird-price-panel' : 'program-price'"
            >
              <template v-if="isProgramEarlyBirdActive">
                <div>
                  <div class="program-early-bird-label">早鳥優惠</div>
                  <div class="program-early-bird-price">
                    {{ money(program.DefaultEarlyBirdPrice) }}
                  </div>
                </div>
                <div class="program-early-bird-meta">
                  <span>截止 {{ formatDate(program.DefaultEarlyBirdEnd) || "未設定" }}</span>
                  <span>售價 {{ money(program.DefaultProductPrice) }}</span>
                </div>
              </template>
              <template v-else>
                {{ money(program.DefaultProductPrice) }}
              </template>
            </div>
            <div
              class="program-detail-content"
              v-html="
                program.Description || program.Memo || '目前尚未提供方案說明。'
              "
            ></div>
          </b-card-body>

          <b-card-body v-if="hasProgramExtraInfo" class="pt-0">
            <b-tabs class="program-detail-tabs mt-3" content-class="pt-3">
              <b-tab v-if="program.Notice" title="注意事項">
                <div
                  class="program-detail-content"
                  v-html="program.Notice"
                ></div>
              </b-tab>
              <b-tab v-if="program.AttendPolicy" title="出席規則">
                <div
                  class="program-detail-content"
                  v-html="program.AttendPolicy"
                ></div>
              </b-tab>
              <b-tab v-if="program.RefundPolicy" title="退費規則">
                <div
                  class="program-detail-content"
                  v-html="program.RefundPolicy"
                ></div>
              </b-tab>
            </b-tabs>
          </b-card-body>

          <b-card-body v-if="programAttachments.length > 0" class="pt-0">
            <div class="program-attachment-panel">
              <h3>注意事項</h3>
              <div class="program-attachment-list">
                <button
                  v-for="file in programAttachments"
                  :key="file.fileId"
                  type="button"
                  class="program-attachment-item"
                  @click="downloadProgramAttachment(file)"
                >
                  <span>{{ attachmentTitle(file) }}</span>
                  <b-badge variant="light">下載</b-badge>
                </button>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <div class="section-heading mt-5">
          <h2>可報名梯次</h2>
        </div>

        <b-alert
          v-if="productList.length === 0"
          show
          variant="light"
          class="text-center"
        >
          目前尚無可瀏覽的梯次。
        </b-alert>

        <!-- Product 梯次列表：每張卡片對應 API 回傳的一個 CampProduct。 -->
        <div v-else class="camp-product-list">
          <div
            v-for="(message, index) in programAddonMessages"
            :key="`program-addon-${index}`"
            class="camp-addon-program-hint"
          >
            <span class="camp-addon-program-hint-label">加購優惠</span>
            {{ message }}
          </div>
          <b-card
            v-for="product in productList"
            :key="product.Id"
            no-body
            class="camp-product-card"
          >
            <b-card-body>
              <div
                v-for="(message, index) in productAddonMessages(product.Id)"
                :key="`product-addon-${product.Id}-${index}`"
                class="camp-addon-product-hint"
              >
                <span class="camp-addon-program-hint-label">加購優惠</span>
                {{ message }}
              </div>
              <b-row align-v="center">
                <b-col cols="12" lg="4" class="mb-3 mb-lg-0">
                  <div class="product-title-line">
                    <div>
                      <h3>
                        {{ product.Title || product.Name }}
                      </h3>
                      <div class="product-subtitle">{{ product.Name }}</div>
                    </div>
                    <b-badge
                      :variant="applyStatusVariant(product)"
                      class="product-status-badge"
                    >
                      {{ applyStatusText(product) }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col cols="12" lg="4" class="mb-3 mb-lg-0">
                  <div class="product-info-grid">
                    <div class="product-info-box">
                      <div class="product-info-label">梯次資訊</div>
                      <strong>{{
                        dateRange(product.Start, product.End)
                      }}</strong>
                      <div class="product-info-meta">
                        <span class="product-info-meta-item">
                          <span class="product-info-meta-label">報名</span>
                          {{ dateRange(product.ApplyStart, product.ApplyEnd) }}
                        </span>
                        <span class="product-info-meta-item">
                          <span class="product-info-meta-label">成班</span>
                          {{ peopleText(product.MinPeople) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </b-col>
                <b-col cols="12" lg="4">
                  <div class="product-action-panel">
                    <b-button
                      v-if="hasProductDetails(product)"
                      variant="primary"
                      size="sm"
                      class="schedule-button"
                      @click="toggleSchedules(product)"
                    >
                      {{ isScheduleOpen(product) ? "收合課表" : "查看課表" }}
                    </b-button>
                    <b-button
                      :variant="canApply(product) ? 'success' : 'secondary'"
                      size="sm"
                      class="apply-button"
                      :disabled="!canApply(product)"
                      @click.stop="openApplyModal(product)"
                    >
                      {{
                        canApply(product)
                          ? "我要報名"
                          : applyStatusText(product)
                      }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>

              <!-- 課表展開區：第一次展開時才呼叫 Schedule API，之後使用 scheduleMap 快取。 -->
              <b-collapse :visible="isScheduleOpen(product)" class="mt-3">
                <div
                  v-if="productDescription(product)"
                  class="program-detail-content product-description-content mb-3"
                  v-html="productDescription(product)"
                ></div>
                <div
                  v-if="loadingSchedules[product.Id]"
                  class="schedule-loading"
                >
                  <b-spinner small variant="primary"></b-spinner>
                </div>
                <b-alert
                  v-else-if="
                    (scheduleMap[product.Id] || []).length === 0 &&
                      !hasProductDescription(product)
                  "
                  show
                  variant="light"
                  class="mb-0"
                >
                  目前尚未提供課表。
                </b-alert>
                <div
                  v-if="(scheduleMap[product.Id] || []).length > 0"
                  class="schedule-panel"
                >
                  <div class="schedule-panel-title">
                    <span>梯次課表</span>
                  </div>

                  <div class="schedule-table-wrap">
                    <table class="schedule-table">
                      <thead>
                        <tr>
                          <th class="time-head"></th>
                          <!-- 日期欄位由 scheduleMatrix 依 Schedule.Start 產生。 -->
                          <th
                            v-for="day in scheduleMatrix(product.Id).days"
                            :key="day.dateKey"
                          >
                            <div>{{ day.dateText }}</div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- 時段列由 scheduleMatrix 依 Start/End 分組產生。 -->
                        <tr
                          v-for="row in scheduleMatrix(product.Id).rows"
                          :key="row.timeKey"
                        >
                          <th class="time-cell">
                            {{ row.timeText }}
                          </th>
                          <td
                            v-for="cell in row.cells"
                            :key="`${row.timeKey}-${cell.key}`"
                            :class="{
                              empty: !cell.schedule,
                              merged: cell.colspan > 1,
                            }"
                            :colspan="cell.colspan"
                          >
                            <template v-if="cell.schedule">
                              <strong>{{
                                cell.schedule.Title || "營隊課程"
                              }}</strong>
                            </template>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </b-collapse>
            </b-card-body>
          </b-card>
        </div>
      </template>
    </b-container>
    <div class="fixed-bottom-actions">
      <b-button variant="outline-secondary" class="back-button" @click="goBack">
        返回方案列表
      </b-button>
    </div>

    <b-modal
      v-model="applyModal.show"
      title="選擇報名學員"
      hide-footer
      centered
      no-close-on-backdrop
    >
      <!-- loading -->
      <template v-if="applyModal.status === 'loading'">
        <div class="text-center py-4">
          <b-spinner variant="primary"></b-spinner>
        </div>
      </template>

      <!-- empty：無關係人 -->
      <template v-else-if="applyModal.status === 'empty'">
        <p class="text-center text-muted mb-3">尚未建立學員，請先新增。</p>
        <div class="text-center">
          <b-button variant="primary" @click="applyModal.status = 'adding'"
            >新增關係人</b-button
          >
        </div>
      </template>

      <!-- selecting：勾選學員 -->
      <template
        v-else-if="
          applyModal.status === 'selecting' ||
            applyModal.status === 'submitting'
        "
      >
        <b-form-checkbox-group
          v-model="applyModal.selectedRelationIds"
          stacked
          class="apply-relation-list mb-3"
        >
          <div
            v-for="rel in applyModal.relationList"
            :key="rel.Id"
            class="apply-relation-item"
          >
            <b-form-checkbox
              :value="rel.Id"
              :disabled="applyModal.status === 'submitting'"
            >
              {{ rel.Name }}
            </b-form-checkbox>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="apply-relation-edit"
              :disabled="applyModal.status === 'submitting'"
              @click="openEditRelation(rel)"
            >
              編輯
            </b-button>
          </div>
        </b-form-checkbox-group>

        <div class="d-flex justify-content-between align-items-center">
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            :disabled="applyModal.status === 'submitting'"
            @click="openAddRelation"
          >
            ＋ 新增關係人
          </b-button>
          <b-button
            variant="success"
            :disabled="
              applyModal.selectedRelationIds.length === 0 ||
                applyModal.status === 'submitting'
            "
            @click="submitApply"
          >
            <b-spinner
              v-if="applyModal.status === 'submitting'"
              small
              class="mr-1"
            ></b-spinner>
            加入購物車
          </b-button>
        </div>
      </template>

      <!-- success -->
      <template v-else-if="applyModal.status === 'success'">
        <div class="text-center py-2">
          <p class="mb-4">已成功加入購物車！</p>
          <div class="d-flex justify-content-center" style="gap: 0.75rem;">
            <b-button variant="outline-secondary" @click="closeApplyModal"
              >繼續瀏覽</b-button
            >
            <b-button variant="primary" @click="_go('/campCart')"
              >前往購物車</b-button
            >
          </div>
        </div>
      </template>

      <!-- adding / editing / saving：關係人表單 -->
      <template
        v-else-if="
          applyModal.status === 'adding' ||
            applyModal.status === 'editing' ||
            applyModal.status === 'saving'
        "
      >
        <b-alert
          v-if="applyModal.formErrorMessage"
          show
          variant="danger"
          class="mb-3"
        >
          {{ applyModal.formErrorMessage }}
        </b-alert>
        <b-form>
          <b-form-group label="姓名 *">
            <b-form-input
              v-model="applyModal.relationForm.Name"
              placeholder="姓名"
              :disabled="applyModal.status === 'saving'"
            ></b-form-input>
          </b-form-group>
          <b-form-group label="性別 *">
            <b-form-radio-group
              v-model="applyModal.relationForm.Gender"
              :disabled="applyModal.status === 'saving'"
            >
              <b-form-radio :value="1">男</b-form-radio>
              <b-form-radio :value="2">女</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group label="出生日期 *">
            <b-form-input
              v-model="applyModal.relationForm.Birthday"
              type="date"
              :disabled="applyModal.status === 'saving'"
            ></b-form-input>
          </b-form-group>
          <b-form-group
            :label="
              applyModal.product && applyModal.product.IsInsurance
                ? '身分證字號 *'
                : '身分證字號'
            "
          >
            <b-form-input
              v-model="applyModal.relationForm.PersonalId"
              placeholder="身分證字號"
              :disabled="applyModal.status === 'saving'"
            ></b-form-input>
          </b-form-group>
          <b-form-group label="就讀學校 *">
            <b-form-input
              v-model="applyModal.relationForm.School"
              placeholder="就讀學校"
              :disabled="applyModal.status === 'saving'"
            ></b-form-input>
          </b-form-group>
          <b-form-group label="年級 *">
            <b-form-select
              v-model="applyModal.relationForm.Grade"
              :options="gradeOptions"
              :disabled="applyModal.status === 'saving'"
            ></b-form-select>
            <b-form-text v-if="gradeSummerNotice" class="text-danger">
              {{ gradeSummerNotice }}
            </b-form-text>
            <b-form-text
              v-if="applyRelationGradeBirthdayMismatch"
              class="text-warning"
            >
              {{ gradeBirthdayMismatchMessage }}
            </b-form-text>
          </b-form-group>
          <b-form-group v-if="applyRelationShowGradeMemo" label="年級備註 *">
            <b-form-input
              v-model="applyModal.relationForm.GradeMemo"
              placeholder="請說明目前就學狀況"
              :disabled="applyModal.status === 'saving'"
            ></b-form-input>
          </b-form-group>
          <b-row>
            <b-col sm="4">
              <b-form-group label="英文姓氏">
                <b-form-input
                  v-model="applyModal.relationForm.EnglishLastName"
                  placeholder="選填"
                  maxlength="255"
                  :disabled="applyModal.status === 'saving'"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col sm="4">
              <b-form-group label="英文名字">
                <b-form-input
                  v-model="applyModal.relationForm.EnglishFirstName"
                  placeholder="選填"
                  maxlength="255"
                  :disabled="applyModal.status === 'saving'"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col sm="4">
              <b-form-group label="英文別名">
                <b-form-input
                  v-model="applyModal.relationForm.EnglishName"
                  placeholder="選填，例如 Steven"
                  maxlength="255"
                  :disabled="applyModal.status === 'saving'"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-text class="mb-3">姓與名請分開填寫，與護照相同</b-form-text>
        </b-form>
        <div class="d-flex justify-content-end mt-3" style="gap: 0.5rem;">
          <b-button
            variant="outline-secondary"
            :disabled="applyModal.status === 'saving'"
            @click="cancelRelationForm"
          >
            取消
          </b-button>
          <b-button
            variant="primary"
            :disabled="applyModal.status === 'saving'"
            @click="saveRelation"
          >
            <b-spinner
              v-if="applyModal.status === 'saving'"
              small
              class="mr-1"
            ></b-spinner>
            儲存
          </b-button>
        </div>
      </template>

      <!-- error -->
      <template v-else-if="applyModal.status === 'error'">
        <b-alert show variant="danger" class="mb-3">
          <div>{{ applyModal.submitErrorMessage }}</div>
          <ul
            v-if="applyModal.submitErrorDetails.length > 0"
            class="mb-0 mt-2 pl-3"
          >
            <li v-for="(detail, i) in applyModal.submitErrorDetails" :key="i">
              {{ detail.relationName }}：{{ detail.error }}
            </li>
          </ul>
        </b-alert>
        <div class="text-right">
          <b-button variant="primary" @click="applyModal.status = 'selecting'"
            >重試</b-button
          >
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  apiGetCampProgram,
  apiGetCampProgramAttachments,
  apiDownloadCampProgramAttachment,
  apiGetCampProductSchedules,
  apiAddCampCartBatch,
  apiGetUserRelationList,
  apiCheckMember,
  apiCreateUserRelation,
  apiUpdateUserRelation,
} from "@/api/API.js";
import {
  GradeOptions,
  GRADE_BIRTHDAY_MISMATCH_MESSAGE,
  getGradeSummerNotice,
  validateFamilyMemberFields,
  validateEnglishNameParts,
  isGradeBirthdayMismatch,
  isOtherGrade,
} from "@/utils/campEnums.js";

export default {
  name: "CampProgram",
  data() {
    return {
      id: "",
      program: {},
      programAttachments: [],
      productList: [],
      addonRules: [],
      scheduleMap: {},
      openScheduleProductId: "",
      loadingSchedules: {},
      gradeOptions: GradeOptions,
      loading: {
        program: false,
      },
      applyModal: {
        show: false,
        product: null,
        status: "idle",
        relationList: [],
        selectedRelationIds: [],
        submitErrorMessage: "",
        submitErrorDetails: [],
        returnStatus: "empty",
        relationForm: {
          Id: null,
          Name: "",
          Gender: null,
          Birthday: "",
          PersonalId: "",
          School: "",
          Grade: null,
          GradeMemo: "",
          EnglishLastName: "",
          EnglishFirstName: "",
          EnglishName: "",
        },
        formErrorMessage: "",
      },
    };
  },
  computed: {
    hasProgramExtraInfo() {
      return !!(
        this.program.Notice ||
        this.program.AttendPolicy ||
        this.program.RefundPolicy
      );
    },
    programAddonMessages() {
      return this.addonRuleMessages(
        (rule) =>
          rule.TriggerType === "CampProgram" ||
          rule.TriggerType === "AnyCampProduct",
      );
    },
    gradeSummerNotice() {
      return getGradeSummerNotice();
    },
    gradeBirthdayMismatchMessage() {
      return GRADE_BIRTHDAY_MISMATCH_MESSAGE;
    },
    applyRelationGradeBirthdayMismatch() {
      const form = this.applyModal.relationForm;
      return isGradeBirthdayMismatch(form.Grade, form.Birthday);
    },
    applyRelationShowGradeMemo() {
      return isOtherGrade(this.applyModal.relationForm.Grade);
    },
    hasProgramPrice() {
      return (
        this.program.DefaultProductPrice !== null &&
        this.program.DefaultProductPrice !== undefined
      );
    },
    isProgramEarlyBirdActive() {
      return this.isEarlyBirdActive({
        EarlyBird: this.program.DefaultEarlyBird,
        EarlyBirdPrice: this.program.DefaultEarlyBirdPrice,
        EarlyBirdEnd: this.program.DefaultEarlyBirdEnd,
      });
    },
  },
  mounted() {
    if (!this.$route.query.id) {
      this._go("./camps");
      return;
    }

    this.id = this.$route.query.id;
    this.getProgram();
  },
  methods: {
    // 取得單一 Program 詳情與底下可報名 Product 梯次。
    getProgram() {
      this.loading.program = true;
      apiGetCampProgram(this.id)
        .then((res) => {
          this.program = res.data && res.data.Program ? res.data.Program : {};
          this.productList = Array.isArray(res.data && res.data.Products)
            ? res.data.Products
            : [];
          this.productList.forEach((product) => {
            if (Array.isArray(product.CampSchedules)) {
              this.$set(this.scheduleMap, product.Id, product.CampSchedules);
            }
          });
          this.addonRules = Array.isArray(this.program.AddonRules)
            ? this.program.AddonRules
            : [];
          this.getProgramAttachments();
        })
        .catch(() => {
          this.program = {};
          this.programAttachments = [];
          this.productList = [];
          this.addonRules = [];
        })
        .finally(() => {
          this.loading.program = false;
        });
    },
    getProgramAttachments() {
      apiGetCampProgramAttachments(this.id)
        .then((res) => {
          this.programAttachments = this.normalizeProgramAttachments(res.data);
        })
        .catch(() => {
          this.programAttachments = [];
        });
    },
    normalizeProgramAttachments(payload) {
      const rawItems = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.Data)
            ? payload.Data
            : [];

      return rawItems
        .map((item) => {
          const fileId = this.pickFirstValue(item, ["fileId", "FileId", "id", "Id"]);
          const name = this.pickFirstValue(item, ["name", "Name", "title", "Title", "fileName", "FileName"]);
          const description = this.pickFirstValue(item, ["description", "Description"], "");

          if (!fileId || !name) return null;

          return {
            fileId,
            name,
            description,
          };
        })
        .filter((item) => !!item);
    },
    pickFirstValue(source, keys, fallback = "") {
      for (let i = 0; i < keys.length; i++) {
        const value = source && source[keys[i]];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }

      return fallback;
    },
    attachmentTitle(file) {
      return file?.name || "附件";
    },
    getDownloadFileName(file, headers) {
      const disposition =
        (headers && (headers["content-disposition"] || headers["Content-Disposition"])) ||
        "";
      let filename =
        (disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) || [])[1] ||
        (disposition.match(/filename="?([^"]+)"?/i) || [])[1] ||
        "";
      if (filename) {
        filename = decodeURIComponent(filename);
      }
      return filename || this.attachmentTitle(file) || "attachment";
    },
    saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    downloadProgramAttachment(file) {
      if (!file || !file.fileId) {
        alert("附件資料不完整，無法下載");
        return;
      }

      apiDownloadCampProgramAttachment(file.fileId)
        .then((res) => {
          const blob = new Blob([res.data], {
            type: res.headers?.["content-type"] || "application/octet-stream",
          });
          this.saveBlob(blob, this.getDownloadFileName(file, res.headers));
        })
        .catch(() => {
          alert("附件下載失敗");
        });
    },
    goBack() {
      if (this.program && this.program.CampSeriesId) {
        this._go(`./campPrograms?id=${this.program.CampSeriesId}`);
        return;
      }

      this._go("./camps");
    },
    // 展開/收合單一 Product 課表；第一次展開才抓 API，避免重複請求。
    toggleSchedules(product) {
      if (!product || !product.Id) {
        return;
      }

      if (this.openScheduleProductId === product.Id) {
        this.openScheduleProductId = "";
        return;
      }

      this.openScheduleProductId = product.Id;
      if (this.scheduleMap[product.Id]) {
        return;
      }

      this.$set(this.loadingSchedules, product.Id, true);
      apiGetCampProductSchedules(product.Id)
        .then((res) => {
          const schedules = Array.isArray(res.data && res.data.Schedules)
            ? res.data.Schedules
            : [];
          this.$set(this.scheduleMap, product.Id, schedules);
          this.$set(this.loadingSchedules, product.Id, false);
        })
        .catch((err) => {
          this.$set(this.scheduleMap, product.Id, []);
          this.$set(this.loadingSchedules, product.Id, false);
          console.log(err);
        });
    },
    isScheduleOpen(product) {
      return product && this.openScheduleProductId === product.Id;
    },
    productDescription(product) {
      return product && product.Description ? product.Description : "";
    },
    hasProductDetails(product) {
      return (
        this.hasProductDescription(product) ||
        (Array.isArray(product.CampSchedules) &&
          product.CampSchedules.length > 0)
      );
    },
    hasProductDescription(product) {
      if (!product || !product.Description) return false;
      if (/<(img|video|iframe|table)\b/i.test(product.Description)) return true;

      return (
        product.Description.replace(/&nbsp;|<br\s*\/?\s*>|<[^>]*>/gi, "").trim()
          .length > 0
      );
    },
    // 將 Schedule 清單整理成課表矩陣：日期當欄、時間區間當列。
    scheduleMatrix(productId) {
      const schedules = (this.scheduleMap[productId] || [])
        .slice()
        .sort((a, b) => {
          return (
            this.parseDisplayDate(a.Start) - this.parseDisplayDate(b.Start)
          );
        });
      const days = [];
      const rowMap = {};

      schedules.forEach((schedule) => {
        const dateKey = this.dateKey(schedule.Start);
        if (!days.some((day) => day.dateKey === dateKey)) {
          days.push({
            dateKey,
            dateText: this.monthDay(schedule.Start),
          });
        }

        const timeKey = this.timeRange(schedule.Start, schedule.End);
        if (!rowMap[timeKey]) {
          rowMap[timeKey] = {
            timeKey,
            timeText: timeKey,
            start: this.parseDisplayDate(schedule.Start),
            items: {},
          };
        }
        rowMap[timeKey].items[dateKey] = schedule;
      });

      const rows = Object.values(rowMap).sort((a, b) => a.start - b.start);
      rows.forEach((row) => {
        row.cells = this.scheduleRowCells(row, days);
      });

      return {
        days,
        rows,
      };
    },
    // 若同一時段每一天內容都相同，就合併成一格，例如每天午餐休息。
    scheduleRowCells(row, days) {
      const cells = days.map((day) => ({
        key: day.dateKey,
        colspan: 1,
        schedule: row.items[day.dateKey] || null,
      }));

      if (cells.length <= 1 || cells.some((cell) => !cell.schedule)) {
        return cells;
      }

      const firstKey = this.scheduleMergeKey(cells[0].schedule);
      const canMergeAll = cells.every(
        (cell) => this.scheduleMergeKey(cell.schedule) === firstKey,
      );
      if (!canMergeAll) {
        return cells;
      }

      return [
        {
          key: "merged",
          colspan: cells.length,
          schedule: cells[0].schedule,
        },
      ];
    },
    // 合併判斷只看課表標題，避免描述或管理分類影響跨日期合併。
    scheduleMergeKey(schedule) {
      return String(schedule.Title || "")
        .trim()
        .toLowerCase();
    },
    fileBaseUrl() {
      return this.$store.state.FilePath;
    },
    getProgramCover(program) {
      if (!program || !program.Id || !program.Cover) {
        return "./asset/no-image.png";
      }

      return this.coverUrl(program.Cover, "_camp_program", program.Id);
    },
    coverUrl(cover, folder, id) {
      if (cover.startsWith("/")) {
        return `${this.fileBaseUrl()}files${cover}`;
      }

      return `${this.fileBaseUrl()}files/${folder}/${id}/${encodeURIComponent(
        cover,
      )}`;
    },
    setDefaultCover(event) {
      event.target.src = "./asset/no-image.png";
    },
    dateRange(start, end) {
      if (!start && !end) {
        return "未設定";
      }
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    },
    monthDay(value) {
      const date = this.parseDisplayDate(value);
      if (!date) {
        return "";
      }
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    dateKey(value) {
      const date = this.parseDisplayDate(value);
      if (!date) {
        return "empty";
      }
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    },
    timeRange(start, end) {
      const startText = this.formatTime(start);
      const endText = this.formatTime(end);
      if (!startText && !endText) {
        return "時間未定";
      }
      return `${startText} - ${endText}`;
    },
    formatDate(value) {
      const date = this.parseDisplayDate(value);
      if (!date) {
        return "";
      }
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    formatTime(value) {
      const date = this.parseDisplayDate(value);
      if (!date) {
        return "";
      }
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      return `${hour}:${minute}`;
    },
    parseDisplayDate(value) {
      if (!value) {
        return null;
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      const text = String(value);
      const match = text.match(
        /^(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?:?(\d{2})?/,
      );
      if (match) {
        return new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
          Number(match[4] || 0),
          Number(match[5] || 0),
          Number(match[6] || 0),
        );
      }

      const date = new Date(text);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    money(value) {
      const amount = Number(value || 0);
      return `NT$${amount.toLocaleString("zh-TW")}`;
    },
    isEarlyBirdActive(item) {
      if (!item || item.EarlyBird !== true) return false;
      if (item.EarlyBirdPrice === null || item.EarlyBirdPrice === undefined) {
        return false;
      }

      const endDate = this.parseDisplayDate(item.EarlyBirdEnd);
      if (!endDate) return false;

      return endDate >= new Date();
    },
    peopleText(value) {
      if (value === null || value === undefined || value === "") {
        return "未設定";
      }
      return `${Number(value).toLocaleString("zh-TW")} 人`;
    },
    applyStatusText(product) {
      return this.applyStatus(product).text;
    },
    applyStatusVariant(product) {
      return this.applyStatus(product).variant;
    },
    applyStatus(product) {
      if (!product || product.Enable !== true) {
        return { text: "未開放", variant: "secondary", canApply: false };
      }

      const now = new Date();
      const start = this.parseDisplayDate(product.ApplyStart);
      const end = this.parseDisplayDate(product.ApplyEnd);

      if (start && now < start) {
        return { text: "尚未開放報名", variant: "secondary", canApply: false };
      }
      if (end && now > end) {
        return { text: "停止招生", variant: "secondary", canApply: false };
      }
      if (product.IsSoldOut === true) {
        return { text: "額滿", variant: "danger", canApply: false };
      }
      return { text: "招生中", variant: "success", canApply: true };
    },
    canApply(product) {
      return this.applyStatus(product).canApply;
    },
    productAddonMessages(productId) {
      return this.addonRuleMessages(
        (rule) =>
          rule.TriggerType === "CampProduct" && rule.TriggerId === productId,
      );
    },
    addonRuleMessages(predicate) {
      return this.addonRules
        .filter(predicate)
        .map((rule) => this.addonRuleMessage(rule))
        .filter((message) => !!message);
    },
    addonRuleMessage(rule) {
      const items = Array.isArray(rule.Items) ? rule.Items : [];
      const validItems = items
        .filter(
          (item) => item.AddonPrice !== null && item.AddonPrice !== undefined,
        )
        .sort((a, b) => Number(a.AddonPrice) - Number(b.AddonPrice));

      if (validItems.length === 0) return "";

      const triggerName =
        rule.TriggerName || this.addonTriggerTypeText(rule.TriggerType);
      const item = validItems[0];
      const addonProductName =
        item.CampProductTitle || item.CampProductName || "加購梯次";
      const addonName = item.CampProgramName
        ? `${item.CampProgramName} ${addonProductName}`
        : addonProductName;
      return `購買${triggerName}可用 ${this.money(
        item.AddonPrice,
      )} 加購 ${addonName}`;
    },
    addonTriggerTypeText(triggerType) {
      const map = {
        AnyCampProduct: "任一梯次",
        CampProgram: "此方案",
        CampProduct: "此梯次",
      };
      return map[triggerType] || "指定項目";
    },
    openApplyModal(product) {
      apiCheckMember()
        .then(() => {
          this.applyModal.product = product;
          this.applyModal.selectedRelationIds = [];
          this.applyModal.submitErrorMessage = "";
          this.applyModal.submitErrorDetails = [];
          this.applyModal.show = true;
          this.loadRelationList();
        })
        .catch(() => {
          alert("請先登入會員");
          this._go("/");
        });
    },
    loadRelationList() {
      this.applyModal.status = "loading";
      apiGetUserRelationList({ Paging: { pageSize: 9999, pageNumber: 1 } })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : [];
          this.applyModal.relationList = list;
          this.applyModal.status = list.length > 0 ? "selecting" : "empty";
          this.applyModal.returnStatus =
            list.length > 0 ? "selecting" : "empty";
        })
        .catch(() => {
          this.applyModal.relationList = [];
          this.applyModal.status = "empty";
          this.applyModal.returnStatus = "empty";
        });
    },
    openAddRelation() {
      this.applyModal.relationForm = {
        Id: null,
        Name: "",
        Gender: null,
        Birthday: "",
        PersonalId: "",
        School: "",
        Grade: null,
        GradeMemo: "",
        EnglishLastName: "",
        EnglishFirstName: "",
        EnglishName: "",
      };
      this.applyModal.formErrorMessage = "";
      this.applyModal.status = "adding";
    },
    openEditRelation(rel) {
      this.applyModal.relationForm = {
        Id: rel.Id,
        Name: rel.Name || "",
        Gender: rel.Gender || null,
        Birthday: rel.Birthday ? rel.Birthday.substring(0, 10) : "",
        PersonalId: rel.PersonalId || "",
        School: rel.School || "",
        Grade: rel.Grade != null ? rel.Grade : null,
        GradeMemo: rel.GradeMemo || "",
        EnglishLastName: rel.EnglishLastName || "",
        EnglishFirstName: rel.EnglishFirstName || "",
        EnglishName: rel.EnglishName || "",
      };
      this.applyModal.formErrorMessage = "";
      this.applyModal.returnStatus = "selecting";
      this.applyModal.status = "editing";
    },
    cancelRelationForm() {
      this.applyModal.formErrorMessage = "";
      this.applyModal.status = this.applyModal.returnStatus;
    },
    saveRelation() {
      const form = this.applyModal.relationForm;
      form.EnglishLastName = (form.EnglishLastName || "").trim();
      form.EnglishFirstName = (form.EnglishFirstName || "").trim();
      form.EnglishName = (form.EnglishName || "").trim();

      if (!form.Name || !form.Name.trim()) {
        this.applyModal.formErrorMessage = "請輸入姓名";
        return;
      }
      if (!form.Gender) {
        this.applyModal.formErrorMessage = "請選擇性別";
        return;
      }
      if (!form.Birthday || !/^\d{4}-\d{2}-\d{2}$/.test(form.Birthday)) {
        this.applyModal.formErrorMessage = "請輸入正確的出生日期（YYYY-MM-DD）";
        return;
      }
      if (
        this.applyModal.product &&
        this.applyModal.product.IsInsurance &&
        !form.PersonalId.trim()
      ) {
        this.applyModal.formErrorMessage = "此營隊需要旅平險，請輸入身分證字號";
        return;
      }
      const fieldError = validateFamilyMemberFields(
        form.Name,
        form.Birthday,
        form.Gender,
        form.School,
        form.Grade,
        form.GradeMemo,
      );
      const englishNameError = validateEnglishNameParts(
        form.EnglishLastName,
        form.EnglishFirstName,
        form.EnglishName,
      );
      const error = fieldError || englishNameError;
      if (error) {
        this.applyModal.formErrorMessage = error;
        return;
      }
      this.applyModal.formErrorMessage = "";
      const prevStatus = this.applyModal.status;
      this.applyModal.status = "saving";
      const isEdit = !!form.Id;
      const payload = {
        Id: form.Id,
        Name: form.Name.trim(),
        Gender: form.Gender,
        Birthday: form.Birthday,
        PersonalId: form.PersonalId.trim(),
        School: form.School.trim(),
        Grade:
          form.Grade !== "" && form.Grade !== null
            ? parseInt(form.Grade, 10)
            : null,
        GradeMemo: form.GradeMemo,
        EnglishLastName: form.EnglishLastName,
        EnglishFirstName: form.EnglishFirstName,
        EnglishName: form.EnglishName,
      };
      const apiCall = isEdit
        ? apiUpdateUserRelation(payload)
        : apiCreateUserRelation(payload);
      apiCall
        .then(() => {
          this.applyModal.formErrorMessage = "";
          this.loadRelationList();
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.applyModal.formErrorMessage =
            (data && (data.error || data.message || data.title)) ||
            "儲存失敗，請稍後再試";
          this.applyModal.status =
            prevStatus === "saving"
              ? isEdit
                ? "editing"
                : "adding"
              : prevStatus;
        });
    },
    closeApplyModal() {
      this.applyModal.show = false;
      this.applyModal.status = "idle";
    },
    submitApply() {
      if (
        !this.applyModal.product ||
        this.applyModal.selectedRelationIds.length === 0
      ) {
        return;
      }
      this.applyModal.status = "submitting";
      apiAddCampCartBatch(
        this.applyModal.product.Id,
        this.applyModal.selectedRelationIds,
      )
        .then(() => {
          this.applyModal.status = "success";
          this.$store.dispatch("campCart/fetchCart");
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.applyModal.submitErrorMessage =
            (data && data.error) || "加入購物車失敗，請稍後再試";
          this.applyModal.submitErrorDetails =
            data && Array.isArray(data.details) ? data.details : [];
          this.applyModal.status = "error";
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.camp-program-page {
  min-height: 100vh;
  background-color: #f7f9fb;
}

.fixed-bottom-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 1.25rem;
  z-index: 1040;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.back-button {
  border-radius: 6px;
  color: #374151;
  font-weight: 700;
  border-color: rgba(31, 41, 55, 0.16);
  background-color: rgba(255, 255, 255, 0.94);
  box-shadow: 0 16px 36px rgba(31, 41, 55, 0.14);
  pointer-events: auto;
  backdrop-filter: blur(8px);
}

.loading-block {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.program-detail-card,
.camp-product-card {
  overflow: hidden;
  border: 1px solid rgba(31, 41, 55, 0.1);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
}

.program-detail-card {
  width: 100%;
  box-shadow: 0 18px 42px rgba(29, 45, 53, 0.08);

  .card-body {
    width: 100%;
    padding: 1.45rem 1.65rem;
    box-sizing: border-box;
  }
}

.camp-product-card {
  position: relative;
  box-shadow: 0 14px 34px rgba(29, 45, 53, 0.07);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #111827, #9ca3af);
    content: "";
  }

  &:hover {
    border-color: rgba(31, 41, 55, 0.16);
    box-shadow: 0 20px 44px rgba(0, 122, 194, 0.14);
    transform: translateY(-3px);
  }

  .card-body {
    padding: 1.25rem 1.35rem;
  }
}

.program-cover {
  width: 100%;
  aspect-ratio: 16 / 7;
  background-color: #eef3f5;
  overflow: hidden;

  .card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
}

.program-detail-kicker,
.section-kicker {
  display: block;
  margin-bottom: 0.4rem;
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 700;
}

.program-detail-card h2,
.section-heading h2 {
  margin-bottom: 0.75rem;
  color: #1d2d35;
  font-weight: 700;
  letter-spacing: 0;
}

.program-detail-card h2 {
  font-size: 1.6rem;
  text-align: left;
}

.camp-addon-program-hint {
  display: inline-flex;
  align-items: center;
  justify-self: start;
  gap: 0.45rem;
  width: fit-content;
  max-width: 100%;
  padding: 0.4rem 0.65rem;
  border-radius: 6px;
  background-color: #fff3e8;
  color: #9a3412;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.45;
  white-space: normal;
}

.camp-addon-program-hint-label {
  flex: 0 0 auto;
  padding: 0.12rem 0.4rem;
  border-radius: 999px;
  background-color: #c2410c;
  color: #fff;
  font-size: 0.78rem;
  line-height: 1.35;
}

.section-heading h2 {
  margin-bottom: 0;
  font-size: 1.55rem;
}

.program-detail-content {
  color: #4f6068;
  line-height: 1.8;
  font-size: 1rem;
}

.program-detail-content ::v-deep p:last-child {
  margin-bottom: 0;
}

.program-attachment-panel {
  padding-top: 1rem;
  border-top: 1px solid rgba(31, 41, 55, 0.1);

  h3 {
    margin-bottom: 0.85rem;
    color: #1d2d35;
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: 0;
  }
}

.program-attachment-list {
  display: grid;
  gap: 0.6rem;
}

.program-attachment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(31, 41, 55, 0.1);
  border-radius: 6px;
  background-color: #f8fbfc;
  color: #1d2d35;
  text-align: left;
  font-weight: 700;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.camp-product-list {
  display: grid;
  gap: 1rem;
}

.product-title-line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  height: 100%;
  padding: 0.15rem 0;

  h3 {
    margin-bottom: 0.35rem;
    color: #1d2d35;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: 0;
  }
}

.product-status-badge {
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  font-size: 0.86rem;
  font-weight: 700;
  white-space: nowrap;
}

.product-subtitle {
  color: #5d6b73;
  font-size: 0.95rem;
  line-height: 1.6;
}

.product-info-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.15rem;
  height: 100%;
}

.product-info-box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 84px;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(31, 41, 55, 0.1);
  border-left: 3px solid #6b7280;
  border-radius: 8px;
  background-color: #f8fbfc;

  strong {
    margin-bottom: 0.25rem;
    color: #1d2d35;
    font-size: 1.05rem;
    font-weight: 700;
  }

  span {
    color: #61717a;
    font-size: 0.88rem;
    line-height: 1.55;
  }
}

.product-info-meta {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0.15rem;
}

.product-info-meta-item {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  min-width: 0;
  color: #61717a;
  font-size: 0.88rem;
  line-height: 1.55;
}

.product-info-meta-label {
  flex: 0 0 auto;
  color: #4b5a62;
  font-weight: 700;
}

.product-info-label {
  margin-bottom: 0.35rem;
  color: #6d7b82;
  font-size: 0.85rem;
  font-weight: 700;
}

.product-action-panel {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
  min-height: 84px;
  padding: 0.2rem 0;
}

.camp-addon-product-hint {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 0.9rem;
  padding: 0.4rem 0.65rem;
  border-radius: 6px;
  background-color: #fff3e8;
  color: #9a3412;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.45;
  white-space: normal;
}

.program-early-bird-price-panel {
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  max-width: 100%;
  margin: 0.25rem 0 1.25rem;
  padding: 0.8rem 1rem;
  border: 1px solid rgba(214, 55, 69, 0.22);
  border-left: 4px solid #d63745;
  border-radius: 8px;
  background-color: #fff7f2;
}

.program-early-bird-label {
  margin-bottom: 0.2rem;
  color: #b84616;
  font-size: 0.9rem;
  font-weight: 700;
}

.program-early-bird-price {
  color: #d63745;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
}

.program-early-bird-meta {
  display: grid;
  gap: 0.25rem;
  color: #8d4b2f;
  font-size: 0.9rem;
  font-weight: 700;
}

.program-early-bird-meta span:last-child {
  color: #7d888e;
  text-decoration: line-through;
}

.program-price {
  display: inline-block;
  margin: 0.25rem 0 1.25rem;
  padding: 0.55rem 1rem;

  color: #c2410c;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-left: 4px solid #c2410c;
  border-radius: 6px;

  font-size: 1.8rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.02em;

  box-shadow: 0 4px 12px rgba(194, 65, 12, 0.08);
}
.schedule-button,
.apply-button {
  width: 112px;
  margin-top: 0.8rem;
}

.apply-button {
  margin-top: 0.55rem;
}

.schedule-loading {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.schedule-panel {
  padding: 1rem;
  border-radius: 8px;
  background: linear-gradient(180deg, #f5fbff, #eaf5ff);
  border: 1px solid rgba(8, 123, 193, 0.18);
}

.schedule-panel-title {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  color: #075985;
  font-weight: 700;
}

.schedule-table-wrap {
  position: relative;
  width: 100%;
  overflow-x: auto;
}

.schedule-table {
  min-width: 760px;
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background-color: #fff;
  color: #17324a;
}

.schedule-table th,
.schedule-table td {
  border: 1px solid #b9d9f2;
  padding: 0.65rem 0.5rem;
  text-align: center;
  vertical-align: middle;
}

.schedule-table thead th {
  background-color: #087bc1;
  color: #fff;
  font-size: 1.05rem;
}

.schedule-table thead small {
  display: block;
  margin-top: 0.15rem;
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.8rem;
}

.schedule-table .time-head,
.schedule-table .time-cell {
  position: sticky;
  left: 0;
  width: 128px;
  background-color: #0b679f;
  color: #fff;
  font-weight: 700;
  box-shadow: 3px 0 6px rgba(7, 89, 133, 0.12);
}

.schedule-table .time-head {
  z-index: 3;
}

.schedule-table .time-cell {
  z-index: 2;
}

.schedule-table td {
  background-color: #fff;
  font-size: 0.95rem;
  line-height: 1.35;
}

.schedule-table td.empty {
  background-color: #edf6fd;
}

.schedule-table td strong {
  display: inline;
  color: #17324a;
}

.schedule-table td p {
  margin: 0.35rem 0 0;
  color: #576a78;
  font-size: 0.82rem;
}

.schedule-table td small {
  display: block;
  margin-top: 0.3rem;
  color: #607282;
  font-size: 0.82rem;
}

@media (max-width: 767.98px) {
  .fixed-bottom-actions {
    bottom: 1rem;
  }

  .program-detail-card {
    .card-body {
      padding: 1.1rem 1rem;
    }
  }

  .program-cover {
    aspect-ratio: 16 / 9;
  }

  .section-heading,
  .schedule-panel-title {
    align-items: flex-start;
    flex-direction: column;
  }

  .product-info-grid {
    grid-template-columns: 1fr;
  }

  .product-action-panel {
    align-items: stretch;
  }

  .schedule-button,
  .apply-button {
    width: 100%;
  }

  .camp-addon-product-hint {
    text-align: left;
  }

  .program-early-bird-price-panel {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.55rem;
    width: 100%;
  }
}

.apply-relation-list {
  max-height: 280px;
  overflow-y: auto;
}

.apply-relation-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: 0;
  }
}

.apply-relation-edit {
  flex-shrink: 0;
  margin-left: 0.5rem;
}
</style>
